/**
 * ターンの終了理由・エラーコード・表示状態の日本語ラベル（task-end-reason-audit）
 *
 * 未知の値（新しいアプリからの将来の値）は「その他（値）」として表示する。
 */
import type { TaskEndState } from '@kyumo/common';

const REASON_LABELS: Record<string, string> = {
  completed: '完了',
  'user-interrupted': 'ユーザー中断',
  'max-steps-reached': 'ステップ上限',
  'session-budget-exceeded': '利用料上限（セッション）',
  'max-continuations-reached': '継続反復の上限',
  'no-progress': '進捗停滞',
  'gave-up': '達成不能と判断',
  error: 'エラー',
  'app-shutdown': 'アプリ終了',
  'origin-cancelled': '依頼元が取り消し',
};

const ERROR_CODE_LABELS: Record<string, string> = {
  COST_LIMIT_EXCEEDED: '月額上限（利用者）',
  SYSTEM_COST_LIMIT_EXCEEDED: '月額上限（システム全体）',
  RATE_LIMIT: 'レート制限',
  AUTH_ERROR: '認証エラー',
  NETWORK_ERROR: 'ネットワークエラー',
  PROVIDER_ERROR: 'AI サービスのエラー',
  EXECUTION_ERROR: '実行エラー',
  UNKNOWN: '不明',
};

function other(value: string): string {
  return `その他（${value}）`;
}

export function getTurnEndReasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? other(reason);
}

export function getTurnErrorCodeLabel(code: string): string {
  return ERROR_CODE_LABELS[code] ?? other(code);
}

export function getTaskEndStateLabel(state: TaskEndState): string {
  switch (state.kind) {
    case 'running':
      return '実行中';
    case 'stale':
      return '記録途絶';
    case 'unrecorded':
      return '理由不明';
    case 'ended':
      return getTurnEndReasonLabel(state.record.reason);
  }
}

/** 配色の系統。完了だけが success、記録途絶とエラーは alert で強調する */
export type TaskEndStateTone = 'success' | 'caution' | 'alert';

export function getTaskEndStateTone(state: TaskEndState): TaskEndStateTone {
  if (state.kind === 'stale') return 'alert';
  if (state.kind === 'ended') {
    if (state.record.reason === 'completed') return 'success';
    if (state.record.reason === 'error') return 'alert';
  }
  return 'caution';
}

/** 配色は既存のバッジ（削除済み・アクティブ）の系統に揃える */
export const TASK_END_TONE_CLASSES: Record<TaskEndStateTone, string> = {
  success: 'bg-green-500/20 text-green-400',
  caution: 'bg-amber-500/20 text-amber-500',
  alert: 'bg-red-500/20 text-red-400',
};
